/**
 * Investor Templates Tab
 *
 * Read-only display of Encompass Investor Templates linked to an investor.
 * Shows templates with grouped contacts, sync status, and link/unlink functionality.
 * Used in the Edit Investor form when Correspondent channel is enabled.
 */
/* eslint-disable react-refresh/only-export-components */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Plus, FileText, Loader2, Unlink, Mail, AlertTriangle, ExternalLink, Globe, Save, Phone } from 'lucide-react';
import { InvestorService } from '@/services/investorService';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { EncompassContactCard } from '../EncompassContactCard';
import { MergedEncompassContactsCard } from '../MergedEncompassContactsCard';
import { TemplateTradeHeader } from '../EncompassContactsSection';
import { SyncStatusBadge } from '../SyncStatusBadge';
import { EditGoodbyeLetterSettingsDialog } from '../EditGoodbyeLetterSettingsDialog';
import { investorEncompassTemplateService } from '@/services/investorEncompassTemplateService';
import { getGoodbyeLetterSettings, upsertGoodbyeLetterSettings } from '@/services/goodbyeLetterSettingsService';
import {
  CONTACT_GROUP_LABELS,
  CONTACT_TYPE_LABELS,
  type EncompassContactType,
  type EncompassTemplateContact,
  type InvestorEncompassTemplate
} from '@/types/investor-encompass';
import type { GoodbyeLetterSettings } from '@/types/goodbye-letter';
import { PRODUCT_TYPES, type ProductType } from '@/types/investor';
import { isSafeUrl, renderUrl } from '@/utils/goodbye-letter/url';
import { resolveGoodbyePaymentWebsite } from '@/utils/goodbye-letter/website';
import { createComponentLogger } from '@/utils/logger';
import { toast } from 'sonner';

const log = createComponentLogger('InvestorTemplatesTab', 'ui');
const EMPTY_SCOPE_MESSAGE = 'Each linked template must have at least one product assignment. Add product scope to every linked template before saving.';
const DUPLICATE_SCOPE_MESSAGE = 'Each product can only be assigned to one linked template. Remove duplicate product scope before saving.';
const REFRESH_AFTER_SAVE_WARNING = 'Product scope saved, but the template list could not refresh. Refresh the page to confirm the latest scope.';

type TemplateProductAssignmentInput = { templateId: string; product: ProductType };
type TemplateScopeValidationResult =
  | { valid: true; assignments: TemplateProductAssignmentInput[] }
  | { valid: false; message: string };

function activePaymentWebsite(settings: GoodbyeLetterSettings | null | undefined): string {
  if (!settings?.onlinePaymentsEnabled) {
    return '';
  }
  return settings.onlinePaymentsUrl?.trim() ?? '';
}

interface PayByPhoneFallback {
  number: string;
  sourceRole: EncompassContactType;
}

interface PayByPhonePrintPreview {
  number: string;
  source: 'manual' | 'contact';
  sourceRole?: EncompassContactType;
}

function contactPhoneForRole(
  contacts: EncompassTemplateContact[] | undefined,
  role: EncompassContactType
): string {
  return contacts?.find((c) => c.contactType === role)?.phone?.trim() ?? '';
}

function resolvePayByPhoneContactFallback(
  contacts: EncompassTemplateContact[] | undefined,
  paymentRole: EncompassContactType,
  customerServiceRole: EncompassContactType
): PayByPhoneFallback | null {
  const paymentPhone = contactPhoneForRole(contacts, paymentRole);
  if (paymentPhone) {
    return { number: paymentPhone, sourceRole: paymentRole };
  }

  const customerServicePhone = contactPhoneForRole(contacts, customerServiceRole);
  if (customerServicePhone) {
    return { number: customerServicePhone, sourceRole: customerServiceRole };
  }

  return null;
}

function resolvePayByPhonePrintPreview(
  settings: GoodbyeLetterSettings | null | undefined,
  fallback: PayByPhoneFallback | null
): PayByPhonePrintPreview | null {
  if (settings === undefined || settings === null) {
    return null;
  }

  if (settings.payByPhoneEnabled && settings.payByPhoneNumber?.trim()) {
    return {
      number: settings.payByPhoneNumber.trim(),
      source: 'manual'
    };
  }

  if (fallback) {
    return {
      number: fallback.number,
      source: 'contact',
      sourceRole: fallback.sourceRole
    };
  }

  return null;
}

function sortTemplateProducts(products: readonly ProductType[]): ProductType[] {
  const selected = new Set(products);
  return PRODUCT_TYPES.filter((product) => selected.has(product));
}

export function getEffectiveTemplateProducts(template: InvestorEncompassTemplate): ProductType[] {
  const products = template.assignments && template.assignments.length > 0
    ? template.assignments.map((assignment) => assignment.product)
    : template.applicableProducts ?? [];

  return sortTemplateProducts(products);
}

export function buildTemplateProductAssignmentsForScopeSave(
  templates: InvestorEncompassTemplate[],
  editingTemplateId: string,
  editedProducts: ProductType[]
): TemplateProductAssignmentInput[] {
  return templates.flatMap((template) => {
    const products = template.id === editingTemplateId
      ? sortTemplateProducts(editedProducts)
      : getEffectiveTemplateProducts(template);
    return products.map((product) => ({
      templateId: template.id,
      product
    }));
  });
}

export function validateTemplateScopeAssignments(
  templates: InvestorEncompassTemplate[],
  assignments: TemplateProductAssignmentInput[]
): TemplateScopeValidationResult {
  const assignedTemplateIds = new Set(assignments.map((assignment) => assignment.templateId));
  if (templates.some((template) => !assignedTemplateIds.has(template.id))) {
    return { valid: false, message: EMPTY_SCOPE_MESSAGE };
  }

  const assignedProducts = new Set<ProductType>();
  for (const assignment of assignments) {
    if (assignedProducts.has(assignment.product)) {
      return { valid: false, message: DUPLICATE_SCOPE_MESSAGE };
    }
    assignedProducts.add(assignment.product);
  }

  return { valid: true, assignments };
}

interface PaymentWebsiteEditorProps {
  templateId: string;
  settings: GoodbyeLetterSettings | null | undefined;
  loadError?: string;
  syncedPaymentWebsite?: string | null;
  paymentRole: EncompassContactType;
  payByPhonePreview: PayByPhonePrintPreview | null;
  hasPaymentContact: boolean;
  onSaved: (settings: GoodbyeLetterSettings) => void;
  canManage?: boolean;
}

const PaymentWebsiteEditor: React.FC<PaymentWebsiteEditorProps> = ({
  templateId,
  settings,
  loadError,
  syncedPaymentWebsite,
  paymentRole,
  payByPhonePreview,
  hasPaymentContact,
  onSaved,
  canManage = true
}) => {
  const loaded = settings !== undefined && !loadError;
  const activeUrl = activePaymentWebsite(settings);
  const [value, setValue] = useState(activeUrl);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useLayoutEffect(() => {
    setValue(activeUrl);
    setError(null);
  }, [activeUrl]);

  const trimmedValue = value.trim();
  const isDirty = trimmedValue !== activeUrl;
  const rawSyncedWebsite = syncedPaymentWebsite?.trim() ?? '';
  const websiteResolution = resolveGoodbyePaymentWebsite(settings, syncedPaymentWebsite);
  const isAutomatic = websiteResolution.source === 'encompass';
  const hasSyncedHint = rawSyncedWebsite.length > 0;
  const syncedUrlIsSafe = hasSyncedHint && isSafeUrl(rawSyncedWebsite);
  const disabledStoredUrl =
    settings !== undefined &&
    settings !== null &&
    !settings.onlinePaymentsEnabled &&
    !!settings.onlinePaymentsUrl?.trim();

  const saveUrl = async (url: string, automatic = false): Promise<void> => {
    const trimmed = url.trim();
    if (!loaded) {
      return;
    }
    if ((automatic && !isSafeUrl(rawSyncedWebsite)) || (trimmed && !isSafeUrl(trimmed))) {
      setError('Enter a domain like www.example.com or a full https:// URL.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const saved = await upsertGoodbyeLetterSettings(templateId, {
        onlinePaymentsEnabled: automatic || trimmed.length > 0,
        onlinePaymentsUrl: automatic ? null : trimmed || null
      });
      onSaved(saved);
      setValue(activePaymentWebsite(saved));
      toast.success('Payment website saved', {
        description: 'Review Goodbye Letter Settings before generating letters for this investor.'
      });
    } catch (err) {
      log.error('Failed to save payment website', { templateId }, err as Error);
      toast.error('Could not save Payment Website');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <label htmlFor={`payment-website-${templateId}`} className="text-xs font-medium">
            Payment Website
          </label>
          <p id={`payment-website-help-${templateId}`} className="mt-0.5 text-[11px] text-muted-foreground">
            {isAutomatic
              ? `Automatically uses the ${CONTACT_TYPE_LABELS[paymentRole]} website after each Encompass template sync. Enter a URL to override it.`
              : 'Manual website for Goodbye Letters. Use the Encompass website automatically when available.'}
          </p>
        </div>
        <Globe className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      </div>

      {!hasPaymentContact && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            No Payment contact synced from Encompass. You can save a Payment Website, but the Goodbye Letter may
            still require Payment contact address data.
          </span>
        </div>
      )}

      {loadError && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {settings === undefined && !loadError && (
        <p className="mt-3 text-[11px] text-muted-foreground">Loading Goodbye Letter settings...</p>
      )}

      {loaded && payByPhonePreview ? (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-background px-2 py-1.5 text-[11px]">
          <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {payByPhonePreview.source === 'manual' ? (
            <span>
              Pay-by-Phone will print the manual number:{' '}
              <span className="font-medium">{payByPhonePreview.number}</span>
            </span>
          ) : (
            <span>
              Pay-by-Phone will print from {CONTACT_TYPE_LABELS[payByPhonePreview.sourceRole!]} contact:{' '}
              <span className="font-medium">{payByPhonePreview.number}</span>
            </span>
          )}
        </div>
      ) : loaded ? (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-background px-2 py-1.5 text-[11px] text-muted-foreground">
          <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>No Pay-by-Phone contact phone found. Add a phone to the configured Payment or Customer Service contact.</span>
        </div>
      ) : null}

      {disabledStoredUrl && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span>
            A Payment Website is saved in settings but Online Payments is disabled. Enter a URL and save to enable it.
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            disabled={!loaded || saving || !canManage}
            onClick={() => void saveUrl('')}
          >
            Clear stored URL
          </Button>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Input
          id={`payment-website-${templateId}`}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          placeholder={isAutomatic ? 'Optional manual override' : 'www.example.com'}
          aria-describedby={`payment-website-help-${templateId}${error ? ` payment-website-error-${templateId}` : ''}`}
          aria-invalid={!!error}
          disabled={!loaded || saving || !canManage}
          className="h-8 min-w-0 flex-1 text-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0"
          disabled={!loaded || !isDirty || saving || !canManage}
          onClick={() => void saveUrl(value)}
        >
          {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
          Save
        </Button>
      </div>

      {error && <p id={`payment-website-error-${templateId}`} className="mt-1 text-[11px] text-destructive">{error}</p>}

      {isAutomatic && !rawSyncedWebsite && (
        <p role="alert" className="mt-2 text-[11px] text-destructive">
          The configured {CONTACT_TYPE_LABELS[paymentRole]} contact has no website. Add it in Encompass and sync,
          or save a manual override. Goodbye Letters cannot be generated with Online Payments enabled until this is resolved.
        </p>
      )}

      {hasSyncedHint && (
        <div className="mt-3 rounded-md bg-background px-2 py-1.5 text-[11px]">
          {syncedUrlIsSafe ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">{isAutomatic ? 'Automatic from Encompass:' : 'Synced from Encompass:'}</span>
              <a
                href={renderUrl(rawSyncedWebsite)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 break-all text-primary hover:underline"
              >
                {rawSyncedWebsite}
                <ExternalLink className="h-3 w-3" />
              </a>
              {!isAutomatic && <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px]"
                disabled={!loaded || saving || !canManage}
                onClick={() => void saveUrl('', true)}
              >
                Use automatically
              </Button>}
            </div>
          ) : (
            <div className="text-amber-700">
              Synced value is not a valid payment URL: <span className="font-medium">{rawSyncedWebsite}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface InvestorTemplatesTabProps {
  servicerSelectionMode?: 'product_scope' | 'per_loan';
  onServicerSelectionModeChanged?: (mode: 'product_scope' | 'per_loan') => void;
  templates: InvestorEncompassTemplate[];
  investorId: string;
  onLinkClick: () => void;
  onUnlink: (templateId: string) => Promise<void>;
  onSync: (template: InvestorEncompassTemplate) => Promise<void>;
  unlinkingTemplateIds: Set<string>;
  syncingTemplateId: string | null;
  isLoading: boolean;
  canManage?: boolean;
  onTemplatesChanged?: () => Promise<void> | void;
}

export const InvestorTemplatesTab: React.FC<InvestorTemplatesTabProps> = ({
  templates,
  investorId,
  onLinkClick,
  onUnlink,
  onSync,
  unlinkingTemplateIds,
  syncingTemplateId,
  isLoading,
  canManage = true,
  onTemplatesChanged,
  servicerSelectionMode = 'product_scope',
  onServicerSelectionModeChanged
}) => {
  const [selectionMode, setSelectionMode] = useState(servicerSelectionMode);
  const [savingMode, setSavingMode] = useState(false);
  useEffect(() => setSelectionMode(servicerSelectionMode), [investorId, servicerSelectionMode]);
  const perLoan = selectionMode === 'per_loan';
  // ----- Hooks (must run on every render — keep them above any early returns) -----
  const [goodbyeSettings, setGoodbyeSettings] = useState<Record<string, GoodbyeLetterSettings | null>>({});
  const [goodbyeSettingsErrors, setGoodbyeSettingsErrors] = useState<Record<string, string | undefined>>({});
  const [openSettingsTemplateId, setOpenSettingsTemplateId] = useState<string | null>(null);
  const settingsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [editingScopeTemplateId, setEditingScopeTemplateId] = useState<string | null>(null);
  const [scopeDraft, setScopeDraft] = useState<Record<string, ProductType[]>>({});
  const [savingScopeTemplateId, setSavingScopeTemplateId] = useState<string | null>(null);
  const isAnyScopeSaving = savingScopeTemplateId !== null;
  const modeControl = canManage && (
    <div className='max-w-xl space-y-2'>
      <label className='flex items-center gap-2 text-sm font-medium'>
        <Switch
          checked={perLoan}
          disabled={savingMode || savingScopeTemplateId !== null}
          onCheckedChange={async (checked) => {
            const next = checked ? 'per_loan' : 'product_scope';
            setSelectionMode(next);
            setSavingMode(true);
            try {
              await InvestorService.setServicerSelectionMode(investorId, next);
              setEditingScopeTemplateId(null);
              onServicerSelectionModeChanged?.(next);
            } catch (error) {
              setSelectionMode(selectionMode);
              toast.error(
                error instanceof Error ? error.message : 'Could not update servicer selection'
              );
            } finally {
              setSavingMode(false);
            }
          }}
        />
        Servicer chosen per loan
      </label>
      <p className='text-xs text-muted-foreground'>
        Use when one investor sends loans to more than one servicer, such as Verus with Cornerstone
        and Shellpoint, or Mutual of Omaha by property state. Product scope is ignored and the
        servicer is chosen on each Banker Recon.
      </p>
    </div>
  );

  useEffect(() => {
    if (templates.length === 0) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const entries: [string, GoodbyeLetterSettings | null][] = [];
      for (const t of templates) {
        if (goodbyeSettings[t.id] !== undefined) {
          continue;
        }
        try {
          const s = await getGoodbyeLetterSettings(t.id);
          entries.push([t.id, s]);
          if (!cancelled) {
            setGoodbyeSettingsErrors((prev) => ({ ...prev, [t.id]: undefined }));
          }
        } catch (err) {
          log.warn('Failed to load goodbye-letter settings for template', { templateId: t.id }, err as Error);
          if (!cancelled) {
            setGoodbyeSettingsErrors((prev) => ({
              ...prev,
              [t.id]: 'Goodbye Letter Settings could not be loaded. Payment Website editing is disabled.'
            }));
          }
        }
      }
      if (!cancelled && entries.length > 0) {
        setGoodbyeSettings((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.map((t) => t.id).join('|')]);

  const handleGoodbyeSettingsSaved = (settings: GoodbyeLetterSettings) => {
    setGoodbyeSettings((prev) => ({ ...prev, [settings.templateId]: settings }));
    setGoodbyeSettingsErrors((prev) => ({ ...prev, [settings.templateId]: undefined }));
  };

  const startScopeEdit = (template: InvestorEncompassTemplate) => {
    setEditingScopeTemplateId(template.id);
    setScopeDraft((prev) => ({
      ...prev,
      [template.id]: getEffectiveTemplateProducts(template)
    }));
  };

  const setScopeProducts = (templateId: string, products: ProductType[]) => {
    setScopeDraft((prev) => ({
      ...prev,
      [templateId]: sortTemplateProducts(products)
    }));
  };

  const getProductsAssignedToOtherTemplates = (templateId: string): Set<ProductType> =>
    new Set(
      templates
        .filter((template) => template.id !== templateId)
        .flatMap(getEffectiveTemplateProducts)
    );

  const toggleScopeProduct = (template: InvestorEncompassTemplate, product: ProductType) => {
    if (getProductsAssignedToOtherTemplates(template.id).has(product)) {
      return;
    }

    const next = new Set(scopeDraft[template.id] ?? getEffectiveTemplateProducts(template));
    if (next.has(product)) {
      next.delete(product);
    } else {
      next.add(product);
    }
    setScopeProducts(template.id, Array.from(next));
  };

  const selectAllScopeProducts = (template: InvestorEncompassTemplate) => {
    const disabledProducts = getProductsAssignedToOtherTemplates(template.id);
    setScopeProducts(
      template.id,
      PRODUCT_TYPES.filter((product) => !disabledProducts.has(product))
    );
  };

  const deselectAllScopeProducts = (templateId: string) => {
    setScopeProducts(templateId, []);
  };

  const cancelScopeEdit = (templateId: string) => {
    setEditingScopeTemplateId(null);
    setScopeDraft((prev) => {
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
  };

  const saveScopeEdit = async (template: InvestorEncompassTemplate) => {
    const products = sortTemplateProducts(scopeDraft[template.id] ?? getEffectiveTemplateProducts(template));
    if (products.length === 0) {
      toast.error('Assign at least one product to this template');
      return;
    }

    const validation = validateTemplateScopeAssignments(
      templates,
      buildTemplateProductAssignmentsForScopeSave(templates, template.id, products)
    );
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setSavingScopeTemplateId(template.id);
    try {
      try {
        await investorEncompassTemplateService.assignTemplateProducts(investorId, validation.assignments);
      } catch (err) {
        log.error('Failed to save template product scope', { investorId, templateId: template.id }, err as Error);
        toast.error(err instanceof Error ? err.message : 'Could not save template product scope');
        return;
      }

      try {
        await onTemplatesChanged?.();
      } catch (err) {
        log.warn('Template product scope saved but template refresh failed', { investorId, templateId: template.id }, err as Error);
        toast.warning(REFRESH_AFTER_SAVE_WARNING);
        return;
      }

      cancelScopeEdit(template.id);
      toast.success('Template product scope saved');
    } finally {
      setSavingScopeTemplateId(null);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 lw-detail-empty">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Loading templates...</p>
      </div>
    );
  }

  // Render empty state
  if (templates.length === 0) {
    return (
      <div className="space-y-4 lw-detail-stack">
        {/* Header with Link button */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-3"><h2 className="text-lg font-semibold">Linked Encompass Templates</h2>{modeControl}</div>
          {canManage && (
            <Button onClick={onLinkClick} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Link Template
            </Button>
          )}
        </div>

        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center px-3.5 py-12 text-center lw-detail-empty">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-base font-medium mb-2">No Templates Linked</h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-4">
              Link an Encompass Investor Template to automatically sync delivery
              contacts and template settings.
            </p>
            {canManage && (
              <Button onClick={onLinkClick} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Link Template
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderGoodbyeLetterCard = (template: InvestorEncompassTemplate) => {
    const settings = goodbyeSettings[template.id];
    const settingsLoadError = goodbyeSettingsErrors[template.id];
    const payByPhoneFallback = settings
      ? resolvePayByPhoneContactFallback(
          template.contacts,
          settings.paymentContactRole,
          settings.customerServiceContactRole
        )
      : null;
    const payByPhonePreview = resolvePayByPhonePrintPreview(settings, payByPhoneFallback);

    const printedOptions: string[] = [];
    if (settings) {
      if (settings.onlinePaymentsEnabled) {
        printedOptions.push('Online Payments');
      }
      if (payByPhonePreview?.source === 'manual') {
        printedOptions.push('Pay by Phone (manual)');
      } else if (payByPhonePreview?.source === 'contact' && payByPhonePreview.sourceRole) {
        printedOptions.push(`Pay by Phone (from ${CONTACT_TYPE_LABELS[payByPhonePreview.sourceRole]} contact)`);
      } else if (settings.payByPhoneEnabled) {
        printedOptions.push('Pay by Phone (missing number)');
      }
      if (settings.autoDeductionEnabled) {
        printedOptions.push('Auto Deduction');
      }
    }

    return (
      <div className="border-t pt-4 mt-4 lw-detail-separated">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div>
              <h4 className="text-sm font-medium">Goodbye Letter Settings</h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                RESPA Notice of Servicing Transfer configuration for this investor.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={settings === undefined || !!settingsLoadError || !canManage}
            onClick={(event) => {
              settingsTriggerRef.current = event.currentTarget;
              setOpenSettingsTemplateId(template.id);
            }}
          >
            {settings ? 'Edit' : 'Configure'}
          </Button>
        </div>

        {template.syncStatus !== 'synced' && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Template is not in sync (status: <strong>{template.syncStatus}</strong>). Goodbye Letter generation
              will fail until the template is resynced.
            </span>
          </div>
        )}

        {settings ? (
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Payment role:</dt>
            <dd>{CONTACT_TYPE_LABELS[settings.paymentContactRole]}</dd>
            <dt className="text-muted-foreground">Customer service role:</dt>
            <dd>{CONTACT_TYPE_LABELS[settings.customerServiceContactRole]}</dd>
            <dt className="text-muted-foreground">Correspondence role:</dt>
            <dd>{CONTACT_TYPE_LABELS[settings.correspondenceContactRole]}</dd>
            <dt className="text-muted-foreground">Printed options:</dt>
            <dd>{printedOptions.length > 0 ? printedOptions.join(', ') : 'None'}</dd>
            {settings.servicerNameOverride && (
              <>
                <dt className="text-muted-foreground">Servicer name override:</dt>
                <dd>{settings.servicerNameOverride}</dd>
              </>
            )}
          </dl>
        ) : settingsLoadError ? (
          <p className="mt-3 text-xs text-destructive">
            Goodbye Letter Settings could not be loaded. Refresh or retry before editing.
          </p>
        ) : settings === undefined ? (
          <p className="mt-3 text-xs text-muted-foreground italic">
            Loading Goodbye Letter Settings...
          </p>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground italic">
            Not configured yet. Click <strong>Configure</strong> to set up role mappings and payment options.
          </p>
        )}
      </div>
    );
  };

  // Render a single template's contacts (reused logic from EncompassContactsSection)
  const renderTemplateContacts = (
    template: InvestorEncompassTemplate,
    options: { showContactDetails?: boolean } = {}
  ) => {
    const showContactDetails = options.showContactDetails ?? true;
    const groupedContacts = template.contacts
      ? investorEncompassTemplateService.getContactsByGroup(template.contacts)
      : null;
    const paymentContact = template.contacts?.find((c) => c.contactType === 'Payment') ?? null;
    const templateSettings = goodbyeSettings[template.id];
    const payByPhoneFallback = templateSettings
      ? resolvePayByPhoneContactFallback(
          template.contacts,
          templateSettings.paymentContactRole,
          templateSettings.customerServiceContactRole
        )
      : null;
    const payByPhonePreview = resolvePayByPhonePrintPreview(templateSettings, payByPhoneFallback);

    const contactGroups = groupedContacts
      ? Object.entries(groupedContacts).filter(
          ([groupKey, contacts]) =>
            contacts.some((c) => investorEncompassTemplateService.hasContactData(c)) ||
            (groupKey === 'servicing' && !!paymentContact)
        )
      : [];
    const displayGroups: Array<[string, EncompassTemplateContact[]]> = [...contactGroups];

    if (!displayGroups.some(([groupKey]) => groupKey === 'servicing')) {
      const servicingEntry: [string, EncompassTemplateContact[]] = [
        'servicing',
        paymentContact ? [paymentContact] : []
      ];
      const insertIndex = displayGroups.findIndex(([groupKey]) => groupKey === 'insurance' || groupKey === 'other');
      if (insertIndex >= 0) {
        displayGroups.splice(insertIndex, 0, servicingEntry);
      } else {
        displayGroups.push(servicingEntry);
      }
    }

    const paymentWebsiteEditor = (
      <PaymentWebsiteEditor
        templateId={template.id}
        settings={goodbyeSettings[template.id]}
        loadError={goodbyeSettingsErrors[template.id]}
        syncedPaymentWebsite={template.contacts?.find((contact) =>
          contact.contactType === (templateSettings?.paymentContactRole ?? 'Payment')
        )?.website}
        paymentRole={templateSettings?.paymentContactRole ?? 'Payment'}
        payByPhonePreview={payByPhonePreview}
        hasPaymentContact={!!paymentContact}
        onSaved={handleGoodbyeSettingsSaved}
        canManage={canManage}
      />
    );

    const isUnlinking = unlinkingTemplateIds.has(template.id);
    const isSyncing = syncingTemplateId === template.id;
    const effectiveProducts = getEffectiveTemplateProducts(template);
    const draftProducts = scopeDraft[template.id] ?? effectiveProducts;
    const selectedScopeProducts = new Set(draftProducts);
    const disabledScopeProducts = getProductsAssignedToOtherTemplates(template.id);
    const isSavingScope = savingScopeTemplateId === template.id;

    return (
      <Card key={template.id} className="overflow-hidden">
        <CardHeader className="px-3.5 pt-2.5 pb-3 bg-muted/30">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-base font-medium">
                {template.templateName}
              </CardTitle>
              {template.investorCode && (
                <p className="text-sm text-muted-foreground mt-1">
                  Code: {template.investorCode}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <SyncStatusBadge
                status={template.syncStatus}
                lastSyncedAt={template.lastSyncedAt}
                syncError={template.syncError}
                onSync={canManage ? () => onSync(template) : undefined}
                isSyncing={isSyncing}
              />
              {canManage && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => onUnlink(template.id)}
                  disabled={isUnlinking}
                >
                  {isUnlinking ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Unlink className="h-4 w-4" />
                  )}
                  <span className="ml-1">Unlink</span>
                </Button>
              )}
            </div>
          </div>

          {/* Item 14-15 Part C: HMDA Purchaser Type + Loan Trade Management at the top of the card. */}
          <TemplateTradeHeader template={template} className="mt-3" />
          {template.lockPeriodDays !== null && template.lockPeriodDays !== undefined && (
            <div className="mt-2 text-xs text-muted-foreground">Lock: {template.lockPeriodDays} days</div>
          )}

          {perLoan ? <span className="mt-3 inline-flex rounded border px-2 py-1 text-xs">Chosen per loan</span> : (
          <div className="mt-3 rounded-md border bg-background/70 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium">Product Scope</div>
                {editingScopeTemplateId === template.id ? (
                  <div className="mt-2 space-y-3">
                    <div className="lw-product-editor">
                      <div className="lw-product-editor-channel scope">
                        <div className="lw-product-editor-head">
                          <span>Template scope</span>
                          <span className="lw-product-editor-count">{selectedScopeProducts.size} selected</span>
                        </div>
                        <div className="lw-product-checkbox-grid">
                          {PRODUCT_TYPES.map((product) => {
                            const disabledByOtherTemplate = disabledScopeProducts.has(product);
                            return (
                              <label
                                key={`${template.id}-${product}`}
                                className="lw-product-checkbox"
                                title={disabledByOtherTemplate ? 'Assigned to another linked template' : undefined}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedScopeProducts.has(product)}
                                  disabled={isAnyScopeSaving || disabledByOtherTemplate}
                                  onChange={() => toggleScopeProduct(template, product)}
                                />
                                <span>{product}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7"
                        disabled={isAnyScopeSaving}
                        onClick={() => selectAllScopeProducts(template)}
                      >
                        Select all
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7"
                        disabled={isAnyScopeSaving}
                        onClick={() => deselectAllScopeProducts(template.id)}
                      >
                        Deselect all
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="h-7"
                        disabled={isAnyScopeSaving || selectedScopeProducts.size === 0}
                        onClick={() => void saveScopeEdit(template)}
                      >
                        {isSavingScope ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="mr-1 h-3.5 w-3.5" />
                        )}
                        Save scope
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7"
                        disabled={isAnyScopeSaving}
                        onClick={() => cancelScopeEdit(template.id)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {effectiveProducts.length > 0 ? (
                      effectiveProducts.map((product) => (
                        <span
                          key={product}
                          className="rounded border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
                        >
                          {product}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-destructive">No product scope assigned</span>
                    )}
                  </div>
                )}
              </div>
              {canManage && editingScopeTemplateId !== template.id && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0"
                  disabled={isAnyScopeSaving}
                  onClick={() => startScopeEdit(template)}
                >
                  Edit scope
                </Button>
              )}
            </div>
          </div>
          )}
        </CardHeader>

        <CardContent className="px-3.5 pb-2.5 pt-4">
          {!showContactDetails ? (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
                Contact details are shown in the merged contact card above.
              </div>
              {paymentWebsiteEditor}
            </div>
          ) : displayGroups.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6 bg-muted/20 rounded">
              No contact information available in this template.
            </div>
          ) : (
            <div className="space-y-6 lw-detail-groups">
              {displayGroups.map(([groupKey, contacts]) => (
                <div key={groupKey}>
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    {CONTACT_GROUP_LABELS[groupKey] || groupKey}
                  </h4>
                  {contacts.length > 0 && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {contacts.map((contact) => {
                        const displayContact =
                          contact.contactType === 'Payment' &&
                          contact.website?.trim() &&
                          !isSafeUrl(contact.website.trim())
                            ? { ...contact, website: null }
                            : contact;

                        return contact.contactType === 'Payment' ? (
                          <div key={contact.contactType} className="min-w-0 space-y-3">
                            <EncompassContactCard contact={displayContact} />
                            {paymentWebsiteEditor}
                          </div>
                        ) : (
                          <EncompassContactCard
                            key={contact.contactType}
                            contact={contact}
                          />
                        );
                      })}
                    </div>
                  )}
                  {groupKey === 'servicing' && !paymentContact && paymentWebsiteEditor}
                </div>
              ))}
            </div>
          )}

          {renderGoodbyeLetterCard(template)}
        </CardContent>
      </Card>
    );
  };

  const showMergedContacts = templates.length > 1;

  return (
    <div className="space-y-4 lw-detail-stack">
      {/* Header with Link button */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-3"><h2 className="text-lg font-semibold">
          Linked Encompass Templates
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            ({templates.length})
          </span>
        </h2>{modeControl}</div>
        {canManage && (
          <Button onClick={onLinkClick} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Link Template
          </Button>
        )}
      </div>

      {/* Templates list */}
      {showMergedContacts && (
        <MergedEncompassContactsCard templates={templates} />
      )}
      <div className="space-y-4 lw-detail-stack">
        {templates.map((template) =>
          renderTemplateContacts(template, { showContactDetails: !showMergedContacts })
        )}
      </div>

      {openSettingsTemplateId && (
        <EditGoodbyeLetterSettingsDialog
          open
          returnFocusRef={settingsTriggerRef}
          onOpenChange={(o) => !o && setOpenSettingsTemplateId(null)}
          templateId={openSettingsTemplateId}
          servicerSelectionMode={selectionMode}
          availableContactTypes={
            templates.find((t) => t.id === openSettingsTemplateId)?.contacts?.map((c) => c.contactType) ?? []
          }
          contacts={templates.find((t) => t.id === openSettingsTemplateId)?.contacts ?? []}
          onSaved={handleGoodbyeSettingsSaved}
        />
      )}
    </div>
  );
};
